import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FieldArray, Formik, useFormikContext } from "formik";
import React from "react";
import { approveRequest, getRequest } from "src/api/inventory";
import * as Yup from "yup";
import CustomInput from "../CustomInput";
import { Button } from "../ui/button";
import { renderNumber, showSuccessToast } from "src/lib/utils";
import { usePortalContext } from "src/context/PortalContext";

const itemValidator = Yup.object().shape({
	product_id: Yup.string().required("Please select a product"),
	qty: Yup.number().moreThan(0, "Quantity must be greater than 0"),
});

const validationSchema = Yup.object().shape({
	items: Yup.array().of(itemValidator).min(1, "At least one item must be provided"),
});

const ApproveRequest = ({ requestUUID }) => {
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const { data } = useQuery({
		queryKey: ["requests", requestUUID],
		queryFn: () => getRequest(requestUUID),
		suspense: true,
	});

	const details = data?.data;

	const initialValues = {
		uuid: requestUUID,
		items: details?.items?.map((item) => ({ product_id: item.product_id, qty: item.requested_quantity, product: item })) ?? [],
	};

	const { mutate, isLoading } = useMutation(approveRequest, {
		onSuccess: () => {
			showSuccessToast("Request approved successfully");
			queryClient.invalidateQueries(["requests"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<FieldArray
						name="items"
						render={({ push, remove }) => (
							<div className="tw-gap-4 tw-grid">
								<div className="tw-grid tw-divide-y tw-divide-[#33333326]">
									{values.items.map((_, index) => (
										<RequestItem key={index} index={index} remove={() => remove(index)} />
									))}
								</div>
							</div>
						)}
					/>
					<Button disabled={isLoading} className="tw-h-fit tw-mt-3" variant="secondary">
						Approve Request
					</Button>
				</form>
			)}
		</Formik>
	);
};

const RequestItem = ({ index, remove }) => {
	const { values, handleChange } = useFormikContext();

	const item = values.items[index];

	return (
		<div className="tw-flex tw-items-center tw-gap-2 tw-py-3.5">
			<div className="tw-flex-1">
				<p className="tw-font-medium tw-text-[#333333]">{item?.product?.name}</p>
				<span className="tw-text-sm tw-text-primary">Requested Quantity: {renderNumber(item?.product?.requested_quantity)}</span>
			</div>
			<div className="tw-flex tw-gap-3 tw-items-center">
				<p className="tw-text-sm">Quantity:</p>
				<CustomInput
					className="!tw-py-1.5 tw-h-[auto] tw-max-w-[90px]"
					value={item.qty}
					name={`items.${index}.qty`}
					onChange={handleChange}
					type="number"
					inputMode="numeric"
				/>
			</div>
		</div>
	);
};

export default ApproveRequest;
